/**
 * Thin client for the session read-path HTTP API. Every call attaches a
 * fresh Cognito ID token as a bearer token; the HTTP API's JWT authorizer
 * validates it. Further per-session authorization is deferred — at current
 * scale (one campaign, a handful of players) the JWT gate is enough.
 *
 * Errors are thrown as plain Error instances with the server's error
 * message when available, matching the shape used by adminApi.ts.
 */
import { getIdToken } from './auth';
import type { Party, LootItem } from './party';
import type { Character } from './character';
import type { CharacterRuntime } from './runtime';
import type { SessionStatusResponse } from './sessionProtocol';

const BaseUrl = import.meta.env.VITE_API_URL as string;

/**
 * Default session id for the single GoobDoob campaign. When multi-campaign
 * lands, callers will need to pass this explicitly — for now the Party page
 * just reads from this one. Matches the default used by the dev seed script.
 */
export const DEFAULT_SESSION_ID = 'session-goobdoob';

export interface PartySnapshot {
  party: Party;
  loot: LootItem[];
}

/**
 * The profile/runtime pair for a single character. Both are typed as
 * Partial because the seed and the Level Up path may produce partial
 * documents during development; UI code defensive-renders whatever it
 * gets rather than crashing on a missing field.
 */
export interface CharacterBundle {
  id: string;
  /** Cognito sub of the player who owns this character. Null = DM-owned or unassigned. */
  ownerSub?: string | null;
  /** Display name for the owner (email or username). */
  ownerUsername?: string | null;
  profile: Partial<Character> | null;
  runtime: Partial<CharacterRuntime> | null;
}

export interface CharacterList {
  characters: CharacterBundle[];
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${resp.status})`;
    throw new Error(msg);
  }

  return payload as T;
}

export function getPartySnapshot(sessionId: string = DEFAULT_SESSION_ID): Promise<PartySnapshot> {
  return request('GET', `/sessions/${encodeURIComponent(sessionId)}/party`);
}

export function getCharacters(sessionId: string = DEFAULT_SESSION_ID): Promise<CharacterList> {
  return request('GET', `/sessions/${encodeURIComponent(sessionId)}/characters`);
}

/**
 * Mirror of the C# AuditEntry record. Detail is left as `unknown` because
 * each action stuffs its own structured fields in there; the audit panel
 * renders the human `summary` line and only opens `detail` on demand.
 */
export interface AuditEntry {
  id: string;
  ts: string;
  sessionId: string;
  actorSub: string;
  actorUsername: string;
  action: string;
  characterId?: string | null;
  version: number;
  reason?: string | null;
  summary: string;
  detail?: Record<string, unknown> | null;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Cursor to pass as `before` for the next older page; null when there are no more. */
  nextBefore: string | null;
}

/**
 * Reads the most-recent audit entries for a session, newest-first. Pass the
 * previous page's `nextBefore` to walk further back in time. The default
 * limit (50) matches the server's clamp range; the server caps at 200.
 */
export function getAuditLog(
  sessionId: string = DEFAULT_SESSION_ID,
  opts: { limit?: number; before?: string } = {},
): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.before)        params.set('before', opts.before);
  const qs = params.toString();
  const path = `/sessions/${encodeURIComponent(sessionId)}/audit${qs ? `?${qs}` : ''}`;
  return request('GET', path);
}

/**
 * Reads the session's active status and connected players. The ACTIVE row in
 * AppTable determines whether a session is live; the BySession GSI on the
 * connections table provides the connected-players list.
 */
export function getSessionStatus(sessionId: string = DEFAULT_SESSION_ID): Promise<SessionStatusResponse> {
  return request('GET', `/sessions/${encodeURIComponent(sessionId)}/status`);
}

/**
 * Composite endpoint that returns everything the dashboard needs in a single
 * cold-load fetch: session status, character roster, party snapshot, recent
 * audit entries, and (for DMs) lore draft count.
 */
export interface DashboardSummary {
  status: {
    active: boolean;
    startedAt?: string | null;
    currentMapId?: string | null;
    dmUsername?: string | null;
    connectedPlayers: Array<{ sub: string; username: string; role: string }>;
  };
  characters: CharacterBundle[];
  party: Party;
  loot: LootItem[];
  audit: {
    entries: AuditEntry[];
    nextBefore: string | null;
  };
  draftCount: number;
}

export function getDashboardSummary(sessionId: string = DEFAULT_SESSION_ID): Promise<DashboardSummary> {
  return request('GET', `/sessions/${encodeURIComponent(sessionId)}/summary`);
}

// ---- Character creation ----

export interface CreateCharacterRequest {
  name: string;
  classA: string;
  classB: string;
  base?: Record<string, number>;
  conSheet?: 'A' | 'B';
  ownerSub?: string;
  ownerUsername?: string;
}

export interface CreateCharacterResponse {
  id: string;
  name: string;
  ownerSub?: string | null;
  ownerUsername?: string | null;
}

/**
 * Create a new character in the session. Admin-only — the server checks
 * for "admins" group membership. Returns the created character's id.
 */
export function createCharacter(
  body: CreateCharacterRequest,
  sessionId: string = DEFAULT_SESSION_ID,
): Promise<CreateCharacterResponse> {
  return request('POST', `/sessions/${encodeURIComponent(sessionId)}/characters`, body);
}

// ---- Profile update (Level Up) ----

/**
 * Overwrites the PROFILE row for a character. Admin-only. The body is the
 * full Character document (the server stamps PK/SK). Used by the Level Up
 * page to commit sheet changes.
 */
export function updateCharacterProfile(
  characterId: string,
  profile: Record<string, unknown>,
  sessionId: string = DEFAULT_SESSION_ID,
): Promise<{ ok: boolean; characterId: string }> {
  return request(
    'PUT',
    `/sessions/${encodeURIComponent(sessionId)}/characters/${encodeURIComponent(characterId)}/profile`,
    profile,
  );
}
